import useTranslation from "next-translate/useTranslation"
import { useRouter } from "next/router"
import { ReactNode, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Button, Card, CardBody, Spinner } from "reactstrap"

import { IDiscussion, IFeedbackInvitation, IUser } from "@api/schema"
import CollapseCard from "@components/common/CollapseCard"
import Icon from "@components/common/Icon"
import TranslatedHtml from "@components/common/TranslatedHtml"
import { addNotificationAction } from "@redux/actions/notifications"
import { IFormikActions, createModelAction, newLoadCollectionAction } from "@redux/helper/actions"
import { AppState } from "@redux/reducer"
import { selectCollectionUsecaseState } from "@redux/reducer/data"
import { IDiscussionFilterCriteria, selectDiscussionsByUserAndFBI } from "@redux/reducer/feedback"
import { EntityType, OrderTypes } from "@redux/reduxTypes"
import { FeedbackHelper } from "@services/feedbackHelper"
import { formatDate } from "@services/formatDate"
import { uriHashMatchesId } from "@services/util"

import FeedbackInvitationQuestionForm from "./FeedbackInvitationQuestionForm"

interface IProps {
  allowMoreThanOneAnswer?: boolean
  alternativeThankYouMessage?: ReactNode // an thank you message independend of the users answer
  currentUser: IUser
  invitation: IFeedbackInvitation
  simplestPresentation?: boolean // only shows form + button
  showSuccessNotificationToast?: boolean // should the notification toast after sending be shown?
}

/**
 * This component provides a form for the questions of a FeedbackInvitation
 * It triggers the creation of a new Discussion, when the user answers the question
 *
 * Specials:
 * to memorize also anonymous answers from the current user the currentUser is injected to createdBy
 * of the new created discussion in the createEntitySaga
 *
 * @todo: make another functionality test!! (11.06.2023)
 */
const FeedbackInvitationQuestionCard: React.FC<IProps> = (props: IProps) => {
  const { allowMoreThanOneAnswer = false, alternativeThankYouMessage, invitation, currentUser, simplestPresentation = false, showSuccessNotificationToast = true } = props
  const { t } = useTranslation("common")
  const dispatch = useDispatch()
  const router = useRouter()

  const cardId = FeedbackHelper.fbiCardId(invitation) // for html-anchor


  /**
   * state of the request for loaded discussions of the current user to the current invitation:
   * only useful if the user is logged in and the disussions are loaded (not created within the session);
   * if the user is not logged in the API would not give discussions with createdBy===null
   */
  const discussionState = useSelector((state: AppState) =>
    selectCollectionUsecaseState(
      state,
      EntityType.Discussion,
      FeedbackHelper.usecaseKeyForUserDiscussionsOnFBI(currentUser, invitation)
    )
  )

  /** current locally held discussions, maybe created within the current session */
  const currentDiscussionsFromUser = useSelector((state: AppState) =>
    selectDiscussionsByUserAndFBI(
      state,
      currentUser,
      invitation,
    )
  )

  /** contains the newest discussion of a possible list of discussions of the user and the fbi OR discussions from the current session */
  const newestDiscussion = currentDiscussionsFromUser?.[0] || discussionState.getItems<IDiscussion>()?.[0] // get first element of the array

  // memorize, if a succesful submission has finished or no submission has started yet
  // to avoid formik-memory-leak, when form is destroyed by displaying the new discussion
  const [successfulSubmission, setSuccessfulSubmission] = useState(false)

  // to allowMoreThanOneAnswer the discussion is be stored to be possible deleted
  const [currentDiscussion, setCurrentDiscussion] = useState(newestDiscussion)

  useEffect(() => {
    if (invitation && currentUser && !discussionState?.loaded) {
      // load discussion(s) for the current invitation and the current user
      // ordered by newest
      dispatch(newLoadCollectionAction(
        EntityType.Discussion,
        {
          createdBy: currentUser?.id.toString(),
          feedbackInvitation: invitation.id.toString(),
          "order[createdAt]": OrderTypes.Desc,
        } as IDiscussionFilterCriteria,
        FeedbackHelper.usecaseKeyForUserDiscussionsOnFBI(currentUser, invitation)
      ))
    }
  }, [invitation])

  const submitNewDiscussion = (disc: IDiscussion, actions: IFormikActions) => {
    actions.onSuccess = () => {
      setSuccessfulSubmission(true)
      setCurrentDiscussion(disc)
      if (showSuccessNotificationToast) {
        dispatch(addNotificationAction("message.discussion.created", "success"))
      }
    }
    dispatch(createModelAction(EntityType.Discussion, disc, actions))
  }


  /*
  * @todo: check, if anonymous is allowed, if not and user not logged in: show questions as list and do login ->
  * does a not logged-in user see any FBIs, that are not anonymous?
  *
  * if the submission was not yet succesful or the discussion is undefined, display the form.
  * We want to prevent any memory-leak-issue, which can happen, if the form is already destroyed,
  * but the submission has not been send yet.
  */
  const QuestionFormAndAnswer = !successfulSubmission || !currentDiscussion
    ? <FeedbackInvitationQuestionForm // show form, if it is not answered yet
      cardId={cardId}
      currentUser={currentUser}
      invitation={invitation}
      onSubmit={submitNewDiscussion}
      simplestPresentation={simplestPresentation}
    />
    : <div className="common-form"><Card>
      <CardBody>
        {alternativeThankYouMessage
          ? alternativeThankYouMessage
          : <>
            {!simplestPresentation && t("feedback.discussion.alreadyAnswered", { date: formatDate(router.locale, newestDiscussion.createdAt, 'date') })}
            {// if the posts are loaded, assigned to the discussion and available, show questions + answers
              newestDiscussion.initialPost && invitation.questions.map((q, index) => {
                return <p key={"answer_" + index.toString()}><b>{q.question}</b> <br />{newestDiscussion.initialPost.content?.[q.id]}</p>
              })
            }
          </>
        }
        {allowMoreThanOneAnswer &&
          // show button to reset created discussion, to allow another answer
          <div className="right">
            <Button className="primary-btn" type="submit" onClick={() => {
              // reset used discussion and submission-status
              setCurrentDiscussion(null)
              setSuccessfulSubmission(false)
            }
            }>
              <Icon name={"plus"} size={18} />
              {t("feedback.post.answers.giveAnotherAnswer")}
            </Button>
          </div>
        }
      </CardBody>
    </Card>
    </div>

  return invitation && invitation.activeUntil && new Date(invitation.activeUntil) > new Date()
    ? !simplestPresentation
      ? <CollapseCard
        data-testid={"FeedbackInvitationQuestionCard"}
        id={cardId}
        initialOpen={!!newestDiscussion || uriHashMatchesId(cardId)}
        header={
          <div>
            <h3>{invitation.title}</h3>
            <div >
              <span>{t("feedback.feedbackInvitation.activeUntil")} </span>
              <b><span>
                <TranslatedHtml content="default.longDateTime" params={{ value: invitation.activeUntil }} />
              </span></b>
            </div>
            {!newestDiscussion && // show only question-count, if the user has not answered yet
              <div>
                {invitation.questions.length} {t("feedback.feedbackInvitation.questions.label")}
              </div>
            }
          </div>
        }
      >
        {discussionState?.isLoading && <Spinner />}
        {QuestionFormAndAnswer}
      </CollapseCard>
      : QuestionFormAndAnswer
    : null
}

export default FeedbackInvitationQuestionCard
